import type { Transaction } from "@rocicorp/zero";
import { Cron } from "croner";
import { apiFragmentPath, validateApiFragment } from "./apiFragment";
import { userFacingError } from "./authorization";
import { getWorkflowIdForCommit, resolveSlug } from "./connectorSlug";
import {
  dropConnectorUsage,
  recordConnectorUsage,
  usageRef,
} from "./connectorUsages";
import { zql } from "./generatedDrizzleSchema";
import { assertSupplierAvailable } from "./rbac/crossResourceAvailability";
import {
  composeStepConfig,
  isStepTestPath,
  parseStepConfig,
  type StepConfig,
  stepDirFromPath,
} from "./stepConfig";
import {
  computeStepLayers,
  STEP_LAYERS_VERSION,
  stepCodeFiles,
} from "./stepLayers";
import { stepContentHashFromFiles } from "./stepTests/freshness";
import { stripNullBytes } from "./stripNullBytes";
import {
  extractStepDependencies,
  isDependencySourceFile,
} from "./workflowDependencies";

type Ctx = { tx: Transaction; commitId: string; tenantId?: string };
type WriteCtx = Ctx & { tenantId: string };
export type StepFile = { path: string; content: string };

export async function hashBlob(raw: string) {
  const content = stripNullBytes(raw);
  const buf = await crypto.subtle.digest(
    "SHA-256",
    new TextEncoder().encode(content)
  );
  const hash = [...new Uint8Array(buf)]
    .map((b) => b.toString(16).padStart(2, "0"))
    .join("");
  return { hash, content };
}

// Returns "" when the step has no files — won’t match any execution row.
export async function getStepContentHash(
  tx: Transaction,
  commitId: string,
  stepDir: string
): Promise<string> {
  const files = await tx.run(zql.commitFiles.where("commitId", commitId));
  return stepContentHashFromFiles(files, stepDir);
}

export async function upsertBlob(
  tx: Transaction,
  content: string,
  tenantId: string
) {
  const { hash, content: clean } = await hashBlob(content);
  const [existing] = await tx.run(
    zql.blobs.where("hash", hash).where("tenantId", tenantId)
  );
  if (!existing)
    await tx.mutate.blobs.upsert({ hash, content: clean, tenantId });
  return hash;
}

export async function writeCommitFile(
  c: WriteCtx,
  path: string,
  content: string
) {
  const { tx, commitId, tenantId } = c;
  const blobHash = await upsertBlob(tx, content, tenantId);
  const [existing] = await tx.run(
    zql.commitFiles.where("commitId", commitId).where("path", path)
  );
  if (existing?.blobHash === blobHash) return;
  await tx.mutate.commitFiles.upsert({ commitId, path, blobHash, tenantId });
}

async function readCommitFileContent(c: Ctx, path: string) {
  const { tx, commitId } = c;
  const [file] = await tx.run(
    zql.commitFiles.where("commitId", commitId).where("path", path)
  );
  if (!file) return null;
  const [blob] = await tx.run(
    zql.blobs.where("hash", file.blobHash).where("tenantId", file.tenantId)
  );
  return blob?.content ?? null;
}

export async function copyAllCommitFiles(c: WriteCtx, toCommitId: string) {
  const { tx, commitId, tenantId } = c;
  for (const f of await tx.run(zql.commitFiles.where("commitId", commitId)))
    await tx.mutate.commitFiles.insert({
      commitId: toCommitId,
      path: f.path,
      blobHash: f.blobHash,
      tenantId,
    });
  await syncAllFacets({ tx, commitId: toCommitId, tenantId });
}

export async function deleteStepDir(c: Ctx, stepDir: string) {
  const { tx, commitId } = c;
  const allFiles = await tx.run(zql.commitFiles.where("commitId", commitId));
  for (const f of allFiles) {
    if (stepDirFromPath(f.path) === stepDir)
      await tx.mutate.commitFiles.delete({ commitId, path: f.path });
  }
  await clearStepFacets(c, stepDir);
}

export async function readStepConfig(
  c: Ctx,
  stepDir: string
): Promise<StepConfig> {
  const raw = await readCommitFileContent(c, `${stepDir}/config.toml`);
  return raw ? parseStepConfig(raw) : { color: "sky", output: false };
}

export async function updateStepConfig(
  c: WriteCtx,
  stepDir: string,
  updates: Partial<StepConfig>
) {
  const config = await readStepConfig(c, stepDir);
  await writeCommitFile(
    c,
    `${stepDir}/config.toml`,
    composeStepConfig({ ...config, ...updates })
  );
}

export async function listStepDirs(c: Ctx): Promise<string[]> {
  const files = await c.tx.run(zql.commitFiles.where("commitId", c.commitId));
  const dirs = new Set<string>();
  for (const f of files) {
    const dir = stepDirFromPath(f.path);
    if (dir) dirs.add(dir);
  }
  return [...dirs];
}

async function clearStepFacets(c: Ctx, stepDir: string) {
  const { tx, commitId } = c;
  for (const r of await tx.run(
    zql.commitRoutes.where("commitId", commitId).where("stepDir", stepDir)
  ))
    await tx.mutate.commitRoutes.delete({ commitId, route: r.route });
  for (const d of await tx.run(
    zql.commitDependencies.where("commitId", commitId).where("stepDir", stepDir)
  ))
    await tx.mutate.commitDependencies.delete({
      commitId,
      stepDir,
      source: d.source,
      name: d.name,
    });
  for (const l of await tx.run(
    zql.commitLinks.where("commitId", commitId).where("fromStep", stepDir)
  ))
    await tx.mutate.commitLinks.delete({
      commitId,
      fromStep: l.fromStep,
      toStep: l.toStep,
    });
  const stepRef = usageRef.commitStep(commitId, stepDir);
  for (const ca of await tx.run(
    zql.connectedAppUsages
      .where("type", stepRef.type)
      .where("refId", stepRef.refId)
      .where("subKey", stepRef.subKey)
  ))
    await dropConnectorUsage(tx, ca.connectedAppId, stepRef);
  const [schedule] = await tx.run(
    zql.commitSchedules.where("commitId", commitId).where("stepDir", stepDir)
  );
  if (schedule) await tx.mutate.commitSchedules.delete({ commitId, stepDir });
  const [stepLayers] = await tx.run(
    zql.commitStepLayers.where("commitId", commitId).where("stepDir", stepDir)
  );
  if (stepLayers)
    await tx.mutate.commitStepLayers.delete({ commitId, stepDir });
  for (const e of await tx.run(
    zql.commitEmailAddresses
      .where("commitId", commitId)
      .where("stepDir", stepDir)
  ))
    await tx.mutate.commitEmailAddresses.delete({
      commitId,
      localPart: e.localPart,
    });
}

/** `space` only when each space has its own subdomain; `tenant` otherwise. */
export type RouteScope = "space" | "tenant";

/**
 * Call from write sites only (never copy paths — the source was already
 * validated when written). `commitId` identifies the workflow to exclude.
 */
export async function assertRoutesAvailable(
  tx: Transaction,
  commitId: string,
  incomingRoutes: Iterable<string>,
  scope: RouteScope
) {
  const incoming = new Set(incomingRoutes);
  if (incoming.size === 0) return;
  const [self] = await tx.run(
    zql.workflowCommits
      .where("id", commitId)
      .related("branch", (b) => b.related("workflow"))
  );
  const w = self?.branch?.workflow;
  if (!w) return;
  const base = zql.workflowBranches
    .where("name", "main")
    .where("workflowId", "!=", w.id)
    .where("tenantId", w.tenantId)
    .related("headCommit", (c) => c.related("routes"));
  const scoped = base.whereExists("workflow", (x) => {
    const live = x.where("deletedAt", "IS", null);
    return scope === "space" ? live.where("spaceId", w.spaceId) : live;
  });
  const mains = await tx.run(scoped);
  for (const mb of mains) {
    const collision = mb.headCommit?.routes.find((r) => incoming.has(r.route));
    if (collision)
      throw userFacingError(
        `Route "${collision.route}" is already used by another live workflow.`
      );
  }
}

export async function assertStepRoutesAvailable(
  c: Ctx,
  stepDirs: Iterable<string>,
  scope: RouteScope
) {
  const routes: string[] = [];
  for (const dir of stepDirs) {
    const config = await readStepConfig(c, dir);
    if (config.route) routes.push(config.route);
  }
  await assertRoutesAvailable(c.tx, c.commitId, routes, scope);
}

export async function assertEmailAddressesAvailable(
  tx: Transaction,
  commitId: string,
  incomingLocalParts: Iterable<string>,
  scope: RouteScope
) {
  const incoming = new Set(incomingLocalParts);
  if (incoming.size === 0) return;
  const [self] = await tx.run(
    zql.workflowCommits
      .where("id", commitId)
      .related("branch", (b) => b.related("workflow"))
  );
  const w = self?.branch?.workflow;
  if (!w) return;
  const base = zql.workflowBranches
    .where("name", "main")
    .where("workflowId", "!=", w.id)
    .where("tenantId", w.tenantId)
    .related("headCommit", (c) => c.related("emailAddresses"));
  const scoped = base.whereExists("workflow", (x) => {
    const live = x.where("deletedAt", "IS", null);
    return scope === "space" ? live.where("spaceId", w.spaceId) : live;
  });
  const mains = await tx.run(scoped);
  for (const mb of mains) {
    const collision = mb.headCommit?.emailAddresses.find((e) =>
      incoming.has(e.localPart)
    );
    if (collision) {
      const where = scope === "space" ? "in this space" : "on this deployment";
      throw userFacingError(
        `Email address "${collision.localPart}" is already used by another live workflow ${where}.`
      );
    }
  }
}

export async function assertStepEmailAddressesAvailable(
  c: Ctx,
  stepDirs: Iterable<string>,
  scope: RouteScope
) {
  const localParts: string[] = [];
  for (const dir of stepDirs) {
    const config = await readStepConfig(c, dir);
    if (config.emailAddress) localParts.push(config.emailAddress);
  }
  await assertEmailAddressesAvailable(c.tx, c.commitId, localParts, scope);
}

export async function syncFacets(c: WriteCtx, stepDir: string) {
  await clearStepFacets(c, stepDir);
  const { tx, commitId, tenantId } = c;
  const config = await readStepConfig(c, stepDir);
  const fragment = await readCommitFileContent(c, apiFragmentPath(stepDir));
  if (fragment !== null) {
    const error = validateApiFragment(stepDir, fragment, config);
    if (error) throw userFacingError(error);
  }
  if (config.route) {
    const conflict = (
      await tx.run(
        zql.commitRoutes
          .where("commitId", commitId)
          .where("route", config.route)
      )
    ).find((r) => r.stepDir !== stepDir);
    if (conflict)
      throw userFacingError(
        `Route "${config.route}" is already used by step "${conflict.stepDir}" in this workflow.`
      );
    await tx.mutate.commitRoutes.insert({
      commitId,
      route: config.route,
      stepDir,
      routeType: config.routeType ?? null,
      routeAuth: config.routeAuth ?? "space",
      externalId:
        config.routeAuth === "external_id" ? (config.externalId ?? null) : null,
      tenantId,
    });
  }
  if (config.cron) {
    let nextRunAt: number;
    try {
      nextRunAt =
        new Cron(config.cron).nextRun()?.getTime() ?? Date.now() + 60000;
    } catch {
      throw userFacingError(
        `Step "${stepDir}" has an invalid cron expression "${config.cron}".`
      );
    }
    await tx.mutate.commitSchedules.insert({
      commitId,
      stepDir,
      cronExpression: config.cron,
      nextRunAt,
      tenantId,
    });
  }
  if (config.emailAddress) {
    await tx.mutate.commitEmailAddresses.insert({
      commitId,
      localPart: config.emailAddress,
      stepDir,
      tenantId,
    });
  }
  for (const toStep of config.links ?? [])
    await tx.mutate.commitLinks.insert({
      commitId,
      fromStep: stepDir,
      toStep,
      tenantId,
    });
  const stepFileRows = (
    await tx.run(zql.commitFiles.where("commitId", commitId))
  ).filter((f) => stepDirFromPath(f.path) === stepDir);
  const contents = await Promise.all(
    stepFileRows.map(async (f) => {
      if (isStepTestPath(f.path) && !isDependencySourceFile(f.path))
        return null;
      const [blob] = await tx.run(
        zql.blobs.where("hash", f.blobHash).where("tenantId", f.tenantId)
      );
      return blob?.content ?? null;
    })
  );
  const sourceFiles: StepFile[] = [];
  const filesWithContent: StepFile[] = [];
  stepFileRows.forEach((f, i) => {
    const content = contents[i];
    if (content == null) return;
    if (content && isDependencySourceFile(f.path))
      sourceFiles.push({ path: f.path, content });
    if (!isStepTestPath(f.path))
      filesWithContent.push({ path: f.path, content });
  });
  if (stepDir !== ".notes" && !stepDir.startsWith(".notes/")) {
    const layers = await computeStepLayers(
      stepCodeFiles(stepDir, filesWithContent)
    );
    if (layers) {
      await tx.mutate.commitStepLayers.insert({
        commitId,
        stepDir,
        version: STEP_LAYERS_VERSION,
        contentHash: await stepContentHashFromFiles(stepFileRows, stepDir),
        layers,
        tenantId,
      });
    }
  }
  for (const dep of extractStepDependencies(sourceFiles)) {
    await tx.mutate.commitDependencies.insert({
      commitId,
      stepDir,
      source: dep.source,
      name: dep.name,
      version: dep.version,
      tenantId,
    });
  }
  if (config.connectors?.length) {
    const workflowId = await getWorkflowIdForCommit(tx, commitId);
    const [wf] = await tx.run(zql.workflows.where("id", workflowId));
    const spaceId = wf?.spaceId;
    for (const entry of config.connectors) {
      const connectedAppId =
        (await resolveSlug(tx, workflowId, entry.name)) ?? entry.name;
      const [app] = await tx.run(zql.connectedApps.where("id", connectedAppId));
      if (!app) continue;
      if (spaceId) {
        await assertSupplierAvailable({
          tx,
          tenantId,
          ruleId: "space_uses_connected_apps",
          dependentScopeId: spaceId,
          supplierScopeId: connectedAppId,
          message: `Missing connector access: ${app.name}`,
        });
      }
      await recordConnectorUsage(
        tx,
        connectedAppId,
        usageRef.commitStep(commitId, stepDir),
        tenantId
      );
    }
  }
}

export async function syncAllFacets(c: WriteCtx) {
  for (const dir of await listStepDirs(c)) await syncFacets(c, dir);
}
